/**
 * Top-level entry point for the runtime.
 *
 * `executeFlow` is what target apps call to run a Studio-authored
 * flow. It owns the lifecycle:
 *
 *1. Ensure built-in step handlers are registered (side-effect
 * import of `handlers/index`).
 *2. Build an `ExecutionContext` from the caller-supplied options.
 *3. Drive `runSteps` and translate the outcome into a `FlowResult`.
 *
 * The function never throws; failures are surfaced as
 * `{ ok: false, error }` so callers can branch declaratively.
 */

import type { FlowInfo } from "../types/flow";

// Side-effect import: registers all built-in step handlers exactly once.
import "../handlers/index";

import {
 ExecutionContext,
 type ExecutionContextOptions,
} from "../context/execution-context";
import { RuntimeAbortError, RuntimeError } from "../errors/index";
import { runSteps } from "./flow-runner";
import type { FlowResult } from "./types";

/**
 * Inputs accepted by {@link executeFlow}. Mirrors
 * {@link ExecutionContextOptions} but omits `flow` (taken as a
 * positional argument) for ergonomic parity with prior versions.
 */
export type ExecuteFlowOptions = Omit<ExecutionContextOptions, "flow">;

/**
 * Run a flow with the supplied adapters. Returns a `FlowResult`
 * describing success/failure and any return value.
 *
 * Outcome mapping:
 * - Handler emitted `{ shouldReturn: true, returnValue }` → ok=true, value=returnValue
 * - Handler emitted `{ result }` → ok=true, value=result
 * - No StepResult / void → ok=true (no value)
 * - Caller-triggered abort (RuntimeAbortError) → ok=false, error=RuntimeAbortError
 * - Any other throw → ok=false, error wrapped in RuntimeError
 */
export async function executeFlow(
 flow: FlowInfo,
 options: ExecuteFlowOptions,
): Promise<FlowResult> {
 const ctx = new ExecutionContext({ ...options, flow });

 try {
 const outcome = await runSteps(flow.steps, ctx);

 if (outcome && typeof outcome === "object") {
 if (outcome.shouldReturn && "returnValue" in outcome) {
 return { ok: true, value: outcome.returnValue };
 }
 if ("result" in outcome) {
 return { ok: true, value: outcome.result };
 }
 }

 return { ok: true };
 } catch (err) {
 // Aborts are first-class — surface them without wrapping so callers
 // can branch on `error instanceof RuntimeAbortError`.
 if (err instanceof RuntimeAbortError) {
 ctx.logger.info(`Flow "${flow.id}" aborted`);
 return { ok: false, error: err };
 }

 const error =
 err instanceof Error
 ? err
 : new RuntimeError("UNKNOWN", String(err), err);
 ctx.logger.error(`Flow "${flow.id}" failed`, error);
 return { ok: false, error };
 }
}